import { COLLECTIONS, useSupplyCatalog, pricingText, money } from "@/components/supply/catalogData";
import { CatalogExamples as RawCatalogExamples } from "@/components/supply/CatalogExamples";
import { SafeBoundary } from "@/components/SafeBoundary";

const CatalogExamples = ({ spot }) => <SafeBoundary name={`catalog-examples-${spot}`}><RawCatalogExamples spot={spot} /></SafeBoundary>;

const PackageCard = ({ p }) => (
  <div className="p-6 border flex flex-col" style={{ borderColor: "var(--ss-border)" }} data-testid={`catalog-pkg-${p.key}`}>
    <div className="font-display text-2xl">{p.label}</div>
    <div className="font-display text-4xl mt-1" style={{ color: "var(--ss-rust)" }}>${p.price.toLocaleString()}</div>
    <ul className="mt-4 space-y-1 text-sm flex-1" style={{ color: "var(--ss-cream-dim)" }}>
      {p.includes.map((it) => <li key={it}>• {it}</li>)}
    </ul>
    <div className="mt-4 text-xs" style={{ color: "var(--ss-muted)" }}>Minimum lead time: {p.lead} calendar days · No rush orders</div>
    {p.shirts > 0 && <div className="mt-1 text-xs" style={{ color: "var(--ss-muted)" }}>Shirts S–XL included; 2XL+ substitution +$5 per shirt.</div>}
    {p.collection === "graduation" && !p.includes.some((i) => /backdrop/i.test(i)) && <div className="mt-1 text-xs" style={{ color: "var(--ss-muted)" }} data-testid={`catalog-no-backdrop-${p.key}`}>Backdrop not included. Adding a custom backdrop changes the minimum lead time for the entire order to 21 days.</div>}
    {p.key === "grad_signature" && <div className="mt-1 text-xs" style={{ color: "var(--ss-muted)" }}>6×4 custom backdrop included.</div>}
    {p.addons.length > 0 && (
      <div className="mt-2 text-xs" style={{ color: "var(--ss-muted)" }}>Add-ons: {p.addons.map((a) => `${a.label} (+${money(a.price)})`).join(" · ")}</div>
    )}
  </div>
);

const COLLECTION_NOTES = {
  executive: "Premium Simply Social experiences combining The Social Supply custom products with an inflatable nightclub rental. Graduation stole not included (add one for $85). One Executive Graduation Experience per weekend, subject to nightclub availability.",
  birthday: "Every birthday package includes one coordinated theme/design concept. Tell us your theme direction and share inspiration — children's T-shirts are not included but can be added at regular pricing, along with any other à la carte product.",
};

export const SupplyCatalogView = () => {
  const cat = useSupplyCatalog();
  if (!cat) return null;
  return (
    <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-24 md:pb-32 space-y-20" data-testid="supply-catalog">
      {COLLECTIONS.map(([c, title]) => (
        <div key={c} data-testid={`catalog-collection-${c}`}>
          <div className="font-label text-xs mb-3" style={{ color: "var(--ss-rust)" }}>{title}</div>
          {COLLECTION_NOTES[c] && <p className="mb-6 max-w-3xl text-sm" style={{ color: "var(--ss-cream-dim)" }}>{COLLECTION_NOTES[c]}</p>}
          <CatalogExamples spot={c} />
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {cat.packages.filter((p) => p.collection === c).map((p) => <PackageCard key={p.key} p={p} />)}
          </div>
        </div>
      ))}
      {cat.sections.map((s) => (
        <div key={s.key} data-testid={`catalog-section-${s.key}`}>
          <div className="font-label text-xs mb-3" style={{ color: "var(--ss-rust)" }}>{s.title}</div>
          {s.note && <p className="mb-6 max-w-3xl text-sm" style={{ color: "var(--ss-cream-dim)" }}>{s.note}</p>}
          <CatalogExamples spot={s.key} />
          <div className="grid md:grid-cols-2 gap-x-10">
            {cat.products.filter((p) => p.section === s.key).map((p) => (
              <div key={p.key} className="py-3 border-b" style={{ borderColor: "var(--ss-border)" }} data-testid={`catalog-product-${p.key}`}>
                <div className="flex flex-wrap justify-between gap-3"><span>{p.label}</span><span style={{ color: "var(--ss-rust)" }}>{pricingText(p)}</span></div>
                {p.note && <div className="text-xs mt-1" style={{ color: "var(--ss-muted)" }}>{p.note}</div>}
                {p.lights && <div className="text-xs mt-1" style={{ color: "var(--ss-muted)" }}>Add flashing lights: +$3 per cup.</div>}
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="text-sm max-w-3xl space-y-2" style={{ color: "var(--ss-cream-dim)" }} data-testid="catalog-rules">
        <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>Production, fulfillment & proofs</div>
        <p>Standard orders: minimum 14 calendar days. Any order with a custom backdrop, Grad Signature and Birthday Bash: minimum 21 days. Executive Luxe / Executive Lit: minimum 30 days. The longest applicable lead time applies to the whole order. No rush orders.</p>
        <p>Pickup is free. Local delivery is $10. Shipping is quoted after your order is reviewed; shipping/transit time is in addition to production time.</p>
        <p>Every order includes 2 design revision rounds; additional rounds are $15 each. Production begins only after you approve your final design proof.</p>
      </div>
    </section>
  );
};
